import {
  Avatar,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { Logout as LogoutIcon, Settings } from '@mui/icons-material';
import { useState, MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../../hooks/useAuth';
import { useTranslation } from '../../../hooks/useTranslation';
import { hasPartnerSession } from '../../../services/partnerSession';
import Modal from '../Modal/Modal';

const getInitials = (name?: string, email?: string): string => {
  const source = (name || email || '').trim();
  if (!source) {
    return '?';
  }

  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }

  return source.slice(0, 2).toUpperCase();
};

const UserAvatarMenu: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const isPartnerSession = hasPartnerSession();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const open = Boolean(anchorEl);

  if (!currentUser) {
    return null;
  }

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSettings = () => {
    handleClose();
    navigate('/settings');
  };

  const handleLogoutClick = () => {
    handleClose();
    setLogoutModalOpen(true);
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        size="small"
        aria-label={t('Account menu')}
        aria-controls={open ? 'user-avatar-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        sx={{ p: 0.25 }}
      >
        <Avatar
          alt={currentUser.name || currentUser.email}
          sx={{
            width: 36,
            height: 36,
            fontSize: '0.875rem',
            fontWeight: 700,
            bgcolor: '#0F172A',
            color: '#FFFFFF',
          }}
        >
          {getInitials(currentUser.name, currentUser.email)}
        </Avatar>
      </IconButton>
      <Menu
        id="user-avatar-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 180,
              borderRadius: 2,
              boxShadow: '0px 8px 24px rgba(15, 23, 42, 0.12)',
            },
          },
        }}
      >
        <MenuItem onClick={handleSettings}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('Settings')}</ListItemText>
        </MenuItem>
        {!isPartnerSession && (
          <MenuItem onClick={handleLogoutClick}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('Logout')}</ListItemText>
          </MenuItem>
        )}
      </Menu>
      {!isPartnerSession && (
        <Modal
          showButton={false}
          modalShouldBeOpen={logoutModalOpen}
          setChangeStatusModalOpen={setLogoutModalOpen}
          callback={logout}
          confirmText={t('Logout')}
          denyText={t('Cancel')}
          description={t(
            'This action closes your current session, and safely exits you from the app.'
          )}
          mainPromptText={t('Do you really want to exit the app?')}
        />
      )}
    </>
  );
};

export default UserAvatarMenu;
